import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import StaffAcceptanceCard from '../components/StaffAcceptanceCard';
import AdminResetPasswordDialog from '../components/AdminResetPasswordDialog';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Pencil, Trash2, KeyRound, Search, X, Shield, User as UserIcon, Loader2, Mail, Copy, RotateCw, Clock, History, ChevronDown, ChevronUp, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { company_admin: 'Company Admin', dispatcher: 'Dispatcher', accountant: 'Accountant / CPA' };
const ROLE_BADGE = {
  company_admin: 'bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20',
  dispatcher: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  accountant: 'bg-amber-50 text-amber-700 border-amber-200',
};

const emptyForm = { name: '', email: '', role: 'dispatcher', phone: '', password: '' };

const TeamMemberModal = ({ mode, initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const [sendInvite, setSendInvite] = useState(mode !== 'edit');
  const isEdit = mode === 'edit';

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, role: form.role, phone: form.phone };
        if (form.password) payload.password = form.password;
        const { data } = await axios.put(`${API}/users/${initial.id}`, payload, { withCredentials: true });
        toast.success(`${data.name} updated`);
      } else if (sendInvite) {
        const { data } = await axios.post(`${API}/team-invitations`, {
          name: form.name, email: form.email, role: form.role, phone: form.phone,
        }, { withCredentials: true });
        toast.success(`Invitation sent to ${data.email}`);
      } else {
        await axios.post(`${API}/users`, { ...form, company_id: initial?.company_id }, { withCredentials: true });
        toast.success(`${form.name} added to the team`);
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Save failed';
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="team-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isEdit ? 'Edit team member' : 'Add team member'}
          </h3>
          <button data-testid="team-modal-close" onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {!isEdit && (
            <div className="flex gap-1 p-1 bg-slate-100 rounded-md" data-testid="invite-toggle">
              <button type="button"
                onClick={() => setSendInvite(true)}
                data-testid="toggle-send-invite"
                className={`flex-1 text-xs font-semibold px-3 py-2 rounded ${sendInvite ? 'bg-white shadow text-[#002FA7]' : 'text-slate-600'}`}>
                Send invite email
              </button>
              <button type="button"
                onClick={() => setSendInvite(false)}
                data-testid="toggle-set-password"
                className={`flex-1 text-xs font-semibold px-3 py-2 rounded ${!sendInvite ? 'bg-white shadow text-[#002FA7]' : 'text-slate-600'}`}>
                Set password now
              </button>
            </div>
          )}
          {!isEdit && sendInvite && (
            <p className="text-xs text-slate-500 -mt-1">
              We&apos;ll email a one-time link that lets them set their own password. Recommended.
            </p>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Name</label>
            <input data-testid="team-input-name" required value={form.name} onChange={set('name')}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</label>
            <input data-testid="team-input-email" required type="email" value={form.email} onChange={set('email')}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Role</label>
              <select data-testid="team-input-role" value={form.role} onChange={set('role')}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white">
                <option value="dispatcher">Dispatcher</option>
                <option value="accountant">Accountant / CPA</option>
                <option value="company_admin">Company Admin</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Phone</label>
              <input data-testid="team-input-phone" value={form.phone || ''} onChange={set('phone')}
                placeholder="(555) 867-5309"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>
          {(isEdit || !sendInvite) && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {isEdit ? 'Reset password (optional)' : 'Password'}
              </label>
              <input data-testid="team-input-password" type="password" required={!isEdit}
                value={form.password} onChange={set('password')}
                placeholder={isEdit ? 'Leave blank to keep current password' : ''}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="team-modal-submit"
              className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save changes' : sendInvite ? 'Send invitation' : 'Add member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Team = () => {
  const { user } = useAuth();
  const [resetMember, setResetMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [modal, setModal] = useState(null); // null | {mode:'create'} | {mode:'edit', initial}
  const [showAudit, setShowAudit] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditCategory, setAuditCategory] = useState('all');

  const fetchAudit = useCallback(async (category) => {
    setAuditLoading(true);
    try {
      const qs = category && category !== 'all' ? `?category=${category}&limit=100` : '?limit=100';
      const { data } = await axios.get(`${API}/user-audit-logs${qs}`, { withCredentials: true });
      setAuditLogs(data);
    } catch {
      toast.error('Could not load audit history');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/team-invitations`, { withCredentials: true }),
      ]);
      setMembers(usersRes.data.filter((u) => u.role === 'dispatcher' || u.role === 'company_admin' || u.role === 'accountant'));
      setInvites(invitesRes.data.filter((i) => !i.used));
    } catch (err) {
      toast.error('Could not load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const copyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch {
      window.prompt('Copy this invite link:', link);
    }
  };

  const resendInvite = async (inv) => {
    try {
      await axios.post(`${API}/team-invitations/${inv.id}/resend`, {}, { withCredentials: true });
      toast.success(`Re-sent invite to ${inv.email}`);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend');
    }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke pending invitation to ${inv.email}?`)) return;
    try {
      await axios.delete(`${API}/team-invitations/${inv.id}`, { withCredentials: true });
      toast.success('Invitation revoked');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke');
    }
  };

  const daysUntilExpiry = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
    return days;
  };

  const toggleAudit = async () => {
    const next = !showAudit;
    setShowAudit(next);
    if (next && auditLogs.length === 0) {
      await fetchAudit(auditCategory);
    }
  };

  const changeAuditCategory = async (cat) => {
    setAuditCategory(cat);
    await fetchAudit(cat);
  };

  const auditLabel = (a) => ({
    'user.create': 'Created user',
    'user.update': 'Updated user',
    'user.delete': 'Deleted user',
    'invite.sent': 'Sent invite',
    'invite.resent': 'Resent invite',
    'invite.revoked': 'Revoked invite',
    'invite.accepted': 'Accepted invite',
    'driver.approved': 'Approved driver',
    'driver.deactivated': 'Deactivated driver',
    'payroll.finalized': 'Finalized payroll',
    'payroll.recalculated': 'Recalculated payroll',
    'load.status_override': 'Reopened delivered load',
  }[a] || a);

  const auditColor = (a) => {
    if (a === 'user.delete' || a === 'invite.revoked' || a === 'driver.deactivated' || a === 'load.status_override') return 'text-red-600';
    if (a === 'user.create' || a === 'invite.accepted' || a === 'driver.approved' || a === 'payroll.finalized') return 'text-emerald-600';
    if (a.startsWith('invite.')) return 'text-[#002FA7]';
    if (a.startsWith('payroll.')) return 'text-amber-600';
    return 'text-slate-700';
  };

  const categoryBadge = (c) => {
    const map = {
      team: 'bg-[#002FA7]/10 text-[#002FA7]',
      driver: 'bg-emerald-50 text-emerald-700',
      payroll: 'bg-amber-50 text-amber-700',
      load: 'bg-slate-100 text-slate-700',
    };
    return map[c] || 'bg-slate-100 text-slate-600';
  };

  const downloadAuditCsv = async (e) => {
    e.stopPropagation();
    try {
      const catParam = auditCategory !== 'all' ? `&category=${auditCategory}` : '';
      const res = await axios.get(`${API}/user-audit-logs/export.csv?days=90${catParam}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_${auditCategory}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit CSV downloaded');
    } catch {
      toast.error('Could not export CSV');
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (!q) return true;
      return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
    });
  }, [members, query, roleFilter]);

  const counts = useMemo(() => ({
    total: members.length,
    admins: members.filter((m) => m.role === 'company_admin').length,
    dispatchers: members.filter((m) => m.role === 'dispatcher').length,
  }), [members]);

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try {
      await axios.delete(`${API}/users/${m.id}`, { withCredentials: true });
      toast.success(`${m.name} removed`);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="team-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Team members
            </h1>
            <p className="text-sm text-slate-600 mt-1">Manage dispatchers and company admins.</p>
          </div>
          <button
            data-testid="team-add-btn"
            onClick={() => setModal({ mode: 'create', initial: { ...emptyForm, company_id: user?.company_id } })}
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold text-sm hover:bg-[#00247D] transition-colors self-start"
          >
            <Plus className="w-4 h-4" /> Add member
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Users className="w-4 h-4" /> Total
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="team-count-total">{counts.total}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Shield className="w-4 h-4" /> Admins
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="team-count-admins">{counts.admins}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <UserIcon className="w-4 h-4" /> Dispatchers
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="team-count-dispatchers">{counts.dispatchers}</p>
          </div>
        </div>

        {invites.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6" data-testid="pending-invites-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-amber-50/40">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-600" />
                <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Pending invitations
                </h2>
                <span className="text-xs font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="pending-invites-count">
                  {invites.length}
                </span>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Expires</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const days = daysUntilExpiry(inv.expires_at);
                  const expired = days <= 0;
                  return (
                    <tr key={inv.id} data-testid={`invite-row-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{inv.name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{inv.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${ROLE_BADGE[inv.role] || ''}`}>
                          {ROLE_LABELS[inv.role] || inv.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center gap-1 ${expired ? 'text-red-600' : 'text-slate-600'}`}>
                          <Clock className="w-3 h-3" />
                          {expired ? 'Expired' : `${days} day${days === 1 ? '' : 's'}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                        <button
                          data-testid={`invite-copy-${inv.id}`}
                          onClick={() => copyInviteLink(inv.invite_link)}
                          className="text-slate-600 hover:text-slate-900" title="Copy link">
                          <Copy className="w-4 h-4 inline" />
                        </button>
                        <button
                          data-testid={`invite-resend-${inv.id}`}
                          onClick={() => resendInvite(inv)}
                          className="text-[#002FA7] hover:text-[#00247D]" title="Resend email">
                          <RotateCw className="w-4 h-4 inline" />
                        </button>
                        <button
                          data-testid={`invite-revoke-${inv.id}`}
                          onClick={() => revokeInvite(inv)}
                          className="text-red-600 hover:text-red-800" title="Revoke">
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                data-testid="team-search"
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email…"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none text-sm"
              />
            </div>
            <select
              data-testid="team-role-filter"
              value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none text-sm bg-white"
            >
              <option value="all">All roles</option>
              <option value="company_admin">Company Admin</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="accountant">Accountant / CPA</option>
            </select>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="team-empty">
              {members.length === 0 ? 'No team members yet. Add your first one.' : 'No matches for that filter.'}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Phone</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const isSelf = m.id === user?.id;
                  return (
                    <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">
                        {m.name}{isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{m.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${ROLE_BADGE[m.role]}`}>
                          {ROLE_LABELS[m.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{m.phone || '—'}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button
                          data-testid={`team-edit-${m.id}`}
                          onClick={() => setModal({ mode: 'edit', initial: { ...m, password: '' } })}
                          className="text-[#002FA7] hover:text-[#00247D]"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4 inline" />
                        </button>
                        {!isSelf && m.role !== 'super_admin' && (
                          <button
                            data-testid={`team-reset-password-${m.id}`}
                            onClick={() => setResetMember(m)}
                            className="text-amber-700 hover:text-amber-900 mr-3"
                            title="Reset password (temporary password)"
                          >
                            <KeyRound className="w-4 h-4 inline" />
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            data-testid={`team-delete-${m.id}`}
                            onClick={() => handleDelete(m)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="audit-history-panel">
          <div
            data-testid="audit-history-toggle"
            onClick={toggleAudit}
            role="button" tabIndex={0}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Audit history
              </span>
              <span className="text-xs text-slate-500">who changed what, when</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadAuditCsv}
                data-testid="audit-download-csv"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#002FA7]"
                title="Download last 90 days as CSV"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
              {showAudit ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </div>
          </div>
          {showAudit && (
            <div className="border-t border-slate-200">
              <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Filter:</span>
                {['all', 'team', 'driver', 'payroll', 'load'].map((cat) => (
                  <button
                    key={cat}
                    data-testid={`audit-filter-${cat}`}
                    onClick={(e) => { e.stopPropagation(); changeAuditCategory(cat); }}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      auditCategory === cat
                        ? 'bg-[#002FA7] text-white'
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {auditLoading ? (
                  <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500" data-testid="audit-history-empty">
                    No mutations logged yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {auditLogs.map((log) => (
                      <li key={log.id} data-testid={`audit-row-${log.id}`}
                          className="px-4 py-3 flex items-center gap-3 text-sm">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${categoryBadge(log.category)}`}>
                          {log.category || 'team'}
                        </span>
                        <span className={`font-semibold ${auditColor(log.action)} shrink-0`}>
                          {auditLabel(log.action)}
                        </span>
                        <span className="text-slate-500 truncate flex-1">
                          {log.target_email || log.meta?.load_ref || log.meta?.driver_name || '—'}
                          {log.meta?.changed?.role && (
                            <span className="ml-2 text-xs text-slate-400">
                              → role: {log.meta.changed.role}
                            </span>
                          )}
                          {log.meta?.changed?.password_reset && (
                            <span className="ml-2 text-xs text-slate-400">(password reset)</span>
                          )}
                          {log.meta?.from_status && log.meta?.to_status && (
                            <span className="ml-2 text-xs text-slate-400">
                              {log.meta.from_status} → {log.meta.to_status}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-slate-400 shrink-0">
                          by {log.actor_email || 'system'}
                        </span>
                        <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">
                          {new Date(log.at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {modal && (
          <TeamMemberModal
            mode={modal.mode}
            initial={modal.initial}
            onClose={() => setModal(null)}
            onSaved={fetchMembers}
          />
        )}
        <StaffAcceptanceCard />
      </div>
      {resetMember && <AdminResetPasswordDialog member={resetMember} onClose={() => setResetMember(null)} />}
    </DashboardLayout>
  );
};

export default Team;
